import * as React from 'react'
import * as api from '@owl-nest/api-client/latest'
import styled from 'styled-components'

import { t, tc } from '@owl-nest/localize'
import { MoneyDisplayer } from '@owl-nest/money'
import * as shadow from '@owl-nest/shadow'
import { BREAKPOINTS, RoleTag, styles as S } from '@ulule/owl-kit-components/next'

import * as styles from '../../style'
import { Project } from '../../../../models/project'

type EngineTagProps = {
  children: React.ReactElement
  type: api.SponsorshipType
}

const Price = styled(RoleTag)`
  flex-shrink: 0;
`

const BackerInfo = styled(styles.BackerInfo)`
  flex-wrap: wrap;

  ${S.copy.M.light} {
    display: inline;
  }

  @media screen and ${BREAKPOINTS.TABLET} {
    flex-wrap: nowrap;
  }
`

const Copy = styled.p`
  margin: 0 5px 10px 0;
`

function EngineTag({ type, children }: EngineTagProps): React.ReactElement<EngineTagProps> {
  const tagsTypes = {
    amount: {
      icon: '⚡️',
      text: '+ ',
    },
    prize_winner: {
      icon: '🏆',
      text: t('Winner'),
    },
    doubled_donations: {
      icon: '⚡️',
      text: t('Matched donations'),
    },
    crush: {
      icon: '❤️',
      text: t('Heartthrob'),
    },
    audience: {
      icon: '👏',
      text: t('Crowd favorite prize'),
    },
  }

  return (
    <>
      <span role="img" aria-label="price emoji">
        {tagsTypes[type].icon}
      </span>
      {tagsTypes[type].text}
      {type === 'amount' && children}
    </>
  )
}

type SponsorPropTypes = {
  sponsorship: api.Sponsorship
  project: Project
}

export function Sponsor({ sponsorship, project }: SponsorPropTypes): React.ReactElement<SponsorPropTypes> {
  const sponsorRef = React.useRef(null)

  shadow.usePartnershipTracker(sponsorship.sponsor.user.id, sponsorRef, {
    click: {
      enabled: true,
    },
    eventPosition: 'backer-list',
    impression: {
      enabled: true,
      once: true,
    },
  })

  return (
    <styles.BackerContainer $variant="sponsor">
      {/* Avatar */}
      <styles.Avatar href={sponsorship.sponsor.link}>
        <S.image.Avatar src={sponsorship.sponsor.image.versions?.small.url} type="medium" />
      </styles.Avatar>
      <BackerInfo>
        <Copy ref={sponsorRef}>
          {/* Name */}
          <S.heading.XS as="a" href={sponsorship.sponsor.link} target="_blank" rel="noopener noreferrer">
            {sponsorship.sponsor.user.name}
          </S.heading.XS>
          {/* Label */}
          <RoleTag forwardedAs="span" backgroundColor="primary-red-900">
            {t('Partner')}
          </RoleTag>
          <br />
          {/* Contribution */}
          <S.copy.M.light as="span">
            {tc('supports this project at [price]', {
              price: <MoneyDisplayer amount={sponsorship.amount} project={project} />,
            })}
          </S.copy.M.light>
        </Copy>
        {/* Sponsor engine tag */}
        <Price backgroundColor="primary-sand-500">
          <EngineTag type={sponsorship?.type || api.SponsorshipType.AMOUNT}>
            <MoneyDisplayer amount={sponsorship?.amount} project={project} />
          </EngineTag>
        </Price>
      </BackerInfo>
    </styles.BackerContainer>
  )
}
